import React, { useEffect, useState } from 'react';
import { MultipleChoicePage as PageType, UserProfile } from '../../types/quiz';
import '../../assets/scss/quiz-editorial.scss';

interface Props {
    page: PageType;
    userProfile: UserProfile;
    onAnswer: (value: string[]) => void;
}

const MultipleChoicePage: React.FC<Props> = ({ page, userProfile, onAnswer }) => {
    const savedValue = page.data_field ? userProfile[page.data_field] : undefined;
    const [selected, setSelected] = useState<string[]>(() => (Array.isArray(savedValue) ? savedValue : []));

    useEffect(() => {
        setSelected(Array.isArray(savedValue) ? savedValue : []);
    }, [page.id, savedValue]);

    const handleToggle = (value: string, isExclusive?: boolean) => {
        if (isExclusive) {
            // If exclusive option (like "None of the above")
            setSelected([value]);
        } else {
            if (selected.includes(value)) {
                // Remove
                setSelected(selected.filter((v) => v !== value));
            } else {
                // Add (and remove any exclusive options)
                const newSelected = selected.filter((v) => {
                    const opt = page.options.find((o) => o.value === v);

                    return !opt?.exclusive;
                });

                setSelected([...newSelected, value]);
            }
        }
    };

    const handleSubmit = () => {
        if (selected.length > 0) {
            onAnswer(selected);
        }
    };

    return (
        <div className="page page--multiple-choice">
            <div className="page__container">
                <h2 className="page__question">{page.question}</h2>
                {page.subtitle && <p className="page__subtitle">{page.subtitle}</p>}

                <div className="checkbox-list">
                    {page.options.map((option) => {
                        const isSelected = selected.includes(option.value);

                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={`checkbox-list__option ${
                                    isSelected ? 'checkbox-list__option--selected' : ''
                                }`}
                                onClick={() => handleToggle(option.value, option.exclusive)}
                            >
                                <span className="checkbox-list__check">
                                    {isSelected && (
                                        <svg viewBox="0 0 12 10" fill="none">
                                            <path
                                                d="M1 5L4.5 8.5L11 1.5"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </span>
                                <span className="checkbox-list__label">{option.label}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="page__actions">
                    <button type="button" className="page__button page__button--block" onClick={handleSubmit} disabled={selected.length === 0}>
                        {page.buttonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultipleChoicePage;
